import { useEffect, useState } from "react";
import { useModalHook } from "../../../../../services/hooks/useModal";
import { useAppSelector } from "../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import { isArabicLanguage } from "../../../../../utils/help/localization/localization";
import { numberWithCurrency } from "../../../../../utils/help/pricing/pricing";
import { PaidReceipt } from "../../../../../utils/types";
import { checkIfItemAvailable } from "../PreviousOrdersHelper";

export type UsePreviousOrderProps = {
  order: PaidReceipt;
  index: number;
  reorder: (index: number, receiptIndex?: number) => void;
};

export const usePreviousOrder = (props: UsePreviousOrderProps) => {
  const { t } = useTranslationI18();
  const { handleModal } = useModalHook();

  const { order } = props;
  const isLanguageArabic = isArabicLanguage();

  const [receiptItems] = useState(order.receiptItems);
  const [disableAddAll, setDisableAddAll] = useState(false);

  const addAll = t("common:add-all");
  const rate = t("common:rate");
  const cancelled = t("common:cancelled");
  const onItsWay = t("delivery:on-its-way");
  const delivered = t("pay:delivered");
  const orderTranslation = t("common:order");
  const totalText = t("common:global-total");

  const currency = useAppSelector((state) => state.config.currency);
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const unRatedReceiptsIds = useAppSelector(
    (state) => state.receipt.unRatedReceiptsIds
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingMethod &&
    !!restaurantInfo?.currencyRoundingLevel;

  const hidePrice = franchiseConfig?.forFeatureShowcase ?? false;

  useEffect(() => {
    for (let i = 0; i < receiptItems.length; i++) {
      const isAvailable = checkIfItemAvailable(
        itemStatusRedux,
        receiptItems[i],
        hasRoundingMethodAndLevel
      );
      if (
        isAvailable === "out-of-stock" ||
        isAvailable === "addon-out-of-stock"
      ) {
        setDisableAddAll(true);
        continue;
      }
    }
  }, []);

  const openFeedbackModal = (receiptId: number, initialStar?: number) => {
    handleModal(true, "FeedBack/FeedBack", {
      componentProps: { receiptId, initialStar },
    });
  };

  const hasFeedback = !unRatedReceiptsIds.includes(order.id);

  const localizedPrice = () => {
    return numberWithCurrency(order.paid_price, currency!);
  };

  return {
    t,
    order,
    receiptItems,
    disableAddAll,
    isLanguageArabic,
    addAll,
    rate,
    cancelled,
    onItsWay,
    delivered,
    orderTranslation,
    totalText,
    currency,
    restaurantInfo,
    hasRoundingMethodAndLevel,
    itemStatusRedux,
    hidePrice,
    hasFeedback,
    openFeedbackModal,
    localizedPrice,
  };
};
